import React from 'react';
import { GraduationCap, School, Award, Scroll, Trophy, Clock, XCircle, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const EDUCATION_LEVELS = [
  { value: 'did_not_graduate', icon: XCircle },
  { value: 'high_school', icon: School },
  { value: 'still_enrolled', icon: Clock },
  { value: 'certificate', icon: Award },
  { value: 'associate', icon: Award },
  { value: 'bachelors', icon: GraduationCap },
  { value: 'masters', icon: Scroll },
  { value: 'doctorate', icon: Trophy },
  { value: 'other_grad_degree', icon: Scroll },
  { value: 'other', icon: HelpCircle },
] as const;

type EducationFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function EducationForm({ formData, onSubmit }: EducationFormProps) {
  const { t } = useTranslation('wizard');
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="education-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="education-title"
          name="education-title"
          as="h1" 
          className="mb-2"
        >
          {t('education.title')}
        </Title>
        <Description 
          id="education-description"
          name="education-description"
          size="lg"
        >
          {t('education.description')}
        </Description>
      </div>

      <div className="grid gap-3">
        {EDUCATION_LEVELS.map(({ value, icon }) => (
          <SelectOption
            key={value}
            id={`education-option-${value}`}
            name={`education-option-${value}`}
            icon={icon}
            label={t(`education.options.${value}.label`)}
            description={t(`education.options.${value}.description`)}
            onClick={() => onSubmit({ educationLevel: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="education-disclaimer"
          name="education-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </div>
  );
}